import React from "react"
import { Table, TableHeaderColumn, TableRow, TableHeader, TableRowColumn, TableBody } from "material-ui"
import strings from "../../../localization/strings"
import autobind from "class-autobind"
import { IScheduleTemplate, ISchedulePatternTemplate, IPosition } from "../../../models"
import ScheduleTemplateDetailExpander from "./schedule-template-detail/ScheduleTemplateDetailExpander"
import * as timeHelper from "../../../helpers/timeHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import * as permissionHelper from "../../../helpers/permissionHelper"
import * as scheduleHelper from "../../../helpers/scheduleHelper"
import * as hierarchyHelper from "../../../helpers/hierarchyHelper"
import AccountStore from "../../../stores/AccountStore"
import { checkJobFieldAvailable } from "../../../helpers/jobHelper"

interface IProps {
	readonly: boolean
	scheduleTemplates: IScheduleTemplate[]
	extendedScheduleTemplateId: string
	handleSelect(scheduleTemplates: IScheduleTemplate[]): void
	handleUpdateExtendedId(id: string): void
	handleAddPatternTemplates(scheduleTemplate: IScheduleTemplate, newPatternTemplates: ISchedulePatternTemplate[], days: number[]): void
	handleDeletePatternTemplates(scheduleTemplate: IScheduleTemplate, selectedPatternTemplates: ISchedulePatternTemplate[]): void
	handleUpdatePatternTemplateQty(scheduleTemplate: IScheduleTemplate, newPatternTemplate: ISchedulePatternTemplate, quantity: number): void
	handleClonePatternTemplates(scheduleTemplate: IScheduleTemplate, clonePatternTemplates: ISchedulePatternTemplate[]): void
	handleUpdateUniquePatternTemplate(scheduleTemplate: IScheduleTemplate, uniquePatternTemplate: ISchedulePatternTemplate): void
}
interface IState {
	selectedScheduleTemplates: IScheduleTemplate[]
	showTable: boolean
}

export default class ScheduleTemplatesTable extends React.Component<IProps, IState> {
	constructor(props) {
		super(props)
		this.state = {
			selectedScheduleTemplates: [],
			showTable: true,
		}
		autobind(this)
	}

	handelOnCellClick(rowNumber, colId) {
		const clickedScheduleTemplate = this.props.scheduleTemplates[rowNumber]
		if (colId === -1) {
			if (!permissionHelper.hasPermission(AuthenticationStore.Permissions.Schedule.DeleteAccountSchedule)) return
			let selectedScheduleTemplates = this.state.selectedScheduleTemplates.slice()
			if (this.state.selectedScheduleTemplates.find((item) => item.Id === clickedScheduleTemplate.Id)) {
				selectedScheduleTemplates = selectedScheduleTemplates.filter((item) => item.Id !== clickedScheduleTemplate.Id)
			}
			else {
				selectedScheduleTemplates.push(clickedScheduleTemplate)
			}
			this.setSelectedScheduleTemplates(selectedScheduleTemplates)
		}
	}

	handleOnRowSelection(result) {
		result === "all" && this.setSelectedScheduleTemplates(this.props.scheduleTemplates.slice())
		result === "none" && this.setSelectedScheduleTemplates([])
	}

	setSelectedScheduleTemplates(selectedScheduleTemplates) {
		this.setState({ selectedScheduleTemplates, showTable: false }, () => this.setState({ showTable: true }))
		this.props.handleSelect(selectedScheduleTemplates)
	}

	render() {
		return (
			<div style={{ position: "relative" }} >
				<Table
					allRowsSelected={this.props.scheduleTemplates.length === this.state.selectedScheduleTemplates.length}
					multiSelectable={!this.props.readonly}
					selectable={!this.props.readonly}
					onCellClick={this.handelOnCellClick}
					onRowSelection={this.handleOnRowSelection}
				>
					<TableHeader>
						<TableRow>
							<TableHeaderColumn className="TextOverflow" title={strings.Name} style={{ textAlign: "left" }}>{strings.Name}</TableHeaderColumn>
							<TableHeaderColumn className="TextOverflow" title={strings.Period_Days} style={{ textAlign: "center", width: 60, paddingLeft: 10, paddingRight: 10 }}>{strings.Period_Days}</TableHeaderColumn>
							<TableHeaderColumn className="TextOverflow" title={strings.Description} style={{ textAlign: "left" }}>{strings.Description}</TableHeaderColumn>
							<TableHeaderColumn className="TextOverflow" title={strings.Hierarchy} style={{ textAlign: "left" }}>{strings.Hierarchy}</TableHeaderColumn>
							{AccountStore.getJobsEnabled() &&
								<TableHeaderColumn className="TextOverflow" title={ checkJobFieldAvailable('singular') ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel}`:  strings.Job} style={{ textAlign: "left" }}>{ checkJobFieldAvailable('singular') ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel}`:  strings.Job}</TableHeaderColumn>
							}
							<TableHeaderColumn className="TextOverflow" title={strings.First_Shift_Start_Time} style={{ textAlign: "center" }}>{strings.First_Shift_Start_Time}</TableHeaderColumn>
							<TableHeaderColumn className="TextOverflow" title={strings.Last_Shift_End_Time} style={{ textAlign: "center" }}>{strings.Last_Shift_End_Time}</TableHeaderColumn>
							<TableHeaderColumn className="TextOverflow" title={strings.Positions} style={{ textAlign: "center" }}>{strings.Positions}</TableHeaderColumn>
							<TableHeaderColumn style={{ width: 30, paddingLeft: 10, paddingRight: 10 }} />
						</TableRow>
					</TableHeader>
					<TableBody showRowHover={false} deselectOnClickaway={false}>
						{this.props.scheduleTemplates.map((row: IScheduleTemplate, index) => {
							const positions: IPosition[] = []
							row.PatternTemplates.forEach(pt =>
								pt.ShiftTemplates.forEach(st =>
									st.Position && !positions.find(e => e.BasePositionId === st.Position.BasePositionId) && positions.push(st.Position)
								)
							)
							const weeklyStartEndTime = row.PatternTemplates.length > 0 ? scheduleHelper.getStartEndSimpleTimeFromPatternTemplates(row.PatternTemplates) : null
							const hierarchyName = hierarchyHelper.getFullyQualifiedLevelNameById(row.HierarchyId)
							return (
								<TableRow
									className="ScheduleTemplatesTable"
									key={index}
									selected={this.state.selectedScheduleTemplates.find((item) => item.Id === row.Id) ? true : false}
								>
									<TableRowColumn style={{ paddingLeft: 0, paddingRight: 0 }}>
										<Table style={{ backgroundColor: "none" }} selectable={true} onCellClick={() => this.props.handleUpdateExtendedId(this.props.extendedScheduleTemplateId === row.Id ? null : row.Id)}>
											<TableBody displayRowCheckbox={false}>
												<TableRow style={{ cursor: "pointer", height: 48, borderBottom: "none" }}>
													<TableRowColumn style={{ textAlign: "left", cursor: "pointer" }}>
														<div className="TextOverflow" title={row.Name}>{row.Name}</div>
													</TableRowColumn>
													<TableRowColumn style={{ textAlign: "center", cursor: "pointer", width: 60, paddingLeft: 10, paddingRight: 10 }}>
														{scheduleHelper.getDayLengthFromStartAndEndTime(row.StartDate, row.EndDate)}
													</TableRowColumn>
													<TableRowColumn style={{ textAlign: "left", cursor: "pointer" }}>
														<div className="TextOverflow" title={row.Description}>{row.Description}</div>
													</TableRowColumn>
													<TableRowColumn style={{ textAlign: "left", cursor: "pointer" }}>
														<div className="TextOverflow" title={hierarchyName}>{hierarchyName}</div>
													</TableRowColumn>
													{AccountStore.getJobsEnabled() &&
														<TableRowColumn style={{ textAlign: "left", cursor: "pointer" }}>
															<div className="TextOverflow" title={row.JobName}>{row.JobName}</div>
														</TableRowColumn>
													}
													<TableRowColumn style={{ textAlign: "center", cursor: "pointer" }}>{weeklyStartEndTime ? timeHelper.getTimeStringFormatFromSimpleTime(weeklyStartEndTime.Start) : "-- : -- --"}</TableRowColumn>
													<TableRowColumn style={{ textAlign: "center", cursor: "pointer" }}>{weeklyStartEndTime ? timeHelper.getTimeStringFormatFromSimpleTime(weeklyStartEndTime.End) : "-- : -- --"}</TableRowColumn>
													<TableRowColumn style={{ textAlign: "center", cursor: "pointer" }}>{positions.length}</TableRowColumn>
													<TableRowColumn style={{ width: 30, paddingLeft: 10, paddingRight: 10 }}>
														<i
															data-intercom-target={index === 0 && "menu-Schedules-ScheduleTemplates-ExpandOrCollapseItemDetails"}
															onClick={(e) => {
																e.stopPropagation()
																this.props.handleUpdateExtendedId(this.props.extendedScheduleTemplateId === row.Id ? null : row.Id)
															}}
															className={this.props.extendedScheduleTemplateId === row.Id ? "fa fa-chevron-up" : "fa fa-chevron-down"}
															style={{ fontSize: 18 }}
														/>
													</TableRowColumn>
												</TableRow>
												{this.props.extendedScheduleTemplateId === row.Id && (
													<TableRow >
														<TableRowColumn
															colSpan={8}
															style={{ paddingRight: 0, paddingLeft: 0 }}
														>
															<div onClick={(e) => { e.stopPropagation() }}>
																<ScheduleTemplateDetailExpander
																	scheduelTemplate={row}
																	handleAddPatternTemplates={this.props.handleAddPatternTemplates}
																	handleDeletePatternTemplates={this.props.handleDeletePatternTemplates}
																	handleUpdatePatternTemplateQty={this.props.handleUpdatePatternTemplateQty}
																	handleClonePatternTemplates={this.props.handleClonePatternTemplates}
																	handleUpdateUniquePatternTemplate={this.props.handleUpdateUniquePatternTemplate}
																/>
															</div>
														</TableRowColumn>
													</TableRow>
												)}
											</TableBody>
										</Table>
									</TableRowColumn>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			</div>
		)
	}
}
